<template>
	<div class="views-analytics-container">

		<SummaryCard
			ref="initial-card"
			:cards-info="todayCardsInfo"
			:data="filteredTodayVisits"
			:previous-data="filteredTodayPreviousVisits"
            :data-field-value="'visits_count'"
			no-currency />

		<FiltersRow ref="filters" @changed-timeframe="changedTimeframe" @switched-chart="tables => showCharts = !tables" with-table-switch :loading="isLoading" />

		<ViewsChartsAggregated :dates="periodDates" v-if="showCharts" />
		<ViewsTablesAggregated :dates="periodDates" v-else />

		<div class="preview-container" v-if="generateHeatMap">
			<MenuPreview always-mobile heat-map ref="heat-map" @loaded="updateHeatMap" @fully-loaded="updateHeatMap" />
		</div>

	</div>
</template>

<script>
import SummaryCard from "@/console-new/components/analytics/SummaryCard.vue";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {useAnalyticsStore, useMenuStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";
import {useLanguageStore} from "@/shared/stores";
import FiltersRow from "@/console-new/components/analytics/FiltersRow.vue";
import ViewsChartsAggregated from "@/console-new/components/analytics/ViewsChartsAggregated.vue";
import ViewsTablesAggregated from "@/console-new/components/analytics/ViewsTablesAggregated.vue";
import MenuPreview from "@/console-new/components/config/menu/MenuPreview.vue";

export default {
	setup() {
		const menuStore = useMenuStore();
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		return {menuStore, analyticsStore, userStore, getString: languageStore.getString};
	},
	name: "ViewsContainer",
	components: {MenuPreview, SummaryCard, FiltersRow, ViewsChartsAggregated, ViewsTablesAggregated },
	inject: ["providedAction"],
	data() {
		return {
			realtimeInterval: null,
			periodDates: {
				end: this.analyticsStore.getTargetEndDate(),
				start: this.analyticsStore.getTargetStartDate(0),
				previousStart: this.analyticsStore.getTargetPreviousStartDate(0),
				previousEnd: this.analyticsStore.getTargetPreviousEndDate(0),
				timeframeIndex: 0,
			},
			showCharts: true,
			generateHeatMap: false,
			isLoading: false,
		};
	},
	computed: {
		todayCardsInfo() {
			return [
				{
					title: this.getString("tot_page_views"),
					value: this.dividedSeries.views,
					previousValue: this.dividedSeries.previousViews,
					noCurrency: true
				},
				{
					title: this.getString("users_number"),
					value: this.dividedSeries.users,
					previousValue: this.dividedSeries.previousUsers,
					noCurrency: true
				},
				{
					title: this.getString("products_clicks"),
					value: this.dividedSeries.productsClicks,
					previousValue: this.dividedSeries.previousProductsClicks,
					noCurrency: true
				},
				{
					title: this.getString("translations"),
					value: this.dividedSeries.translations,
					previousValue: this.dividedSeries.previousTranslations,
					noCurrency: true
				},
			];
		},
		filteredTodayVisits() {
			const now = new Date();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.viewsAggregatedToday.visits_menu || this.analyticsStore.viewsAggregatedToday.visits_menu.length === 0) {
				return [];
			}
			
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);
			
			const filteredData = this.analyticsStore.viewsAggregatedToday.visits_menu.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so 03:00 contains data from 02:00-03:00
				// We want to exclude the shift hour itself to avoid counting pre-shift data
				return date > todayShiftStart && date <= nowRounded;
			});
			
			return filteredData.map(row => ({
				date: new Date(row.hour_bucket || row.day_bucket),
				visits_count: Number(row.visits_count || 0)
			}));
		},
		filteredTodayPreviousVisits() {
			const now = new Date();
			const yesterdayShiftStart = this.analyticsStore.getPreviousDayShiftDatetime();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.viewsAggregatedToday.visits_menu || this.analyticsStore.viewsAggregatedToday.visits_menu.length === 0) {
				return [];
			}

			// Calculate time elapsed since today's shift start
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);

			const elapsedTime = nowRounded.getTime() - todayShiftStart.getTime();
			const yesterdayEndTime = new Date(yesterdayShiftStart.getTime() + elapsedTime);

			const filteredData = this.analyticsStore.viewsAggregatedToday.visits_menu.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so exclude the shift hour itself
				return date > yesterdayShiftStart && date <= yesterdayEndTime;
			});
			
			return filteredData.map(row => ({
				date: new Date(row.hour_bucket || row.day_bucket),
				visits_count: Number(row.visits_count || 0)
			}));
		},
		filteredTodayEvents() {
			const now = new Date();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.viewsAggregatedToday.events || this.analyticsStore.viewsAggregatedToday.events.length === 0) {
				return [];
			}
			
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);
			
			const filteredData = this.analyticsStore.viewsAggregatedToday.events.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so 03:00 contains data from 02:00-03:00
				// We want to exclude the shift hour itself to avoid counting pre-shift data
				return date > todayShiftStart && date <= nowRounded;
			});
			
			return filteredData.map(row => ({
				date: new Date(row.hour_bucket || row.day_bucket),
				item_clicks: Number(row.item_clicks || 0),
				translations_count: Number(row.translations_count || 0)
			}));
		},
		filteredTodayPreviousEvents() {
			const now = new Date();
			const yesterdayShiftStart = this.analyticsStore.getPreviousDayShiftDatetime();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.viewsAggregatedToday.events || this.analyticsStore.viewsAggregatedToday.events.length === 0) {
				return [];
			}

			// Calculate time elapsed since today's shift start
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);

			const elapsedTime = nowRounded.getTime() - todayShiftStart.getTime();
			const yesterdayEndTime = new Date(yesterdayShiftStart.getTime() + elapsedTime);

			const filteredData = this.analyticsStore.viewsAggregatedToday.events.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so exclude the shift hour itself
				return date > yesterdayShiftStart && date <= yesterdayEndTime;
			});
			
			return filteredData.map(row => ({
				date: new Date(row.hour_bucket || row.day_bucket),
				item_clicks: Number(row.item_clicks || 0),
				translations_count: Number(row.translations_count || 0)
			}));
		},
		dividedSeries() {
			let result = {
				views: 0,
				previousViews: 0,
				users: 0,
				previousUsers: 0,
				productsClicks: 0,
				previousProductsClicks: 0,
				translations: 0,
				previousTranslations: 0,
			};

			// Use aggregated data
			this.filteredTodayVisits.forEach(visit => {
				result.views += visit.visits_count;
			});
			this.filteredTodayPreviousVisits.forEach(visit => {
				result.previousViews += visit.visits_count;
			});
			
			this.filteredTodayEvents.forEach(event => {
				result.productsClicks += event.item_clicks;
				result.translations += event.translations_count;
			});
			this.filteredTodayPreviousEvents.forEach(event => {
				result.previousProductsClicks += event.item_clicks;
				result.previousTranslations += event.translations_count;
			});

			return result;
		}
	},
	methods: {
		openHeatMap() {
			this.generateHeatMap = true;

			this.$nextTick(() => {
				this.$refs['heat-map'].showMobilePreview = true;

				let startDateDB = formatDateTimeWithSeconds(this.periodDates.start);
				let endDateDB = formatDateTimeWithSeconds(this.periodDates.end);

				// Always use aggregated heatmap
				this.analyticsStore.getStatsHeatMapAggregated(startDateDB, endDateDB, () => {
					this.updateHeatMap();
				});
			});

		},
		updateHeatMap() {
			if(!this.generateHeatMap) {
				return;
			}

			let iframe = this.$refs && this.$refs['heat-map'] ? this.$refs['heat-map'].currentIframe : null;
			if(iframe) {
				let points = this.analyticsStore.heatMap.slice();
				let data = {
					type: "heatmap",
					data: points,
				}
				iframe.contentWindow.postMessage(data, window.location.protocol+ "//" + window.location.host);
			}
		},
		updateTodayViews(callback = null) {
			let now = new Date();
			let endDate = new Date(now);
			if (endDate.getMinutes() > 0 || endDate.getSeconds() > 0) {
				endDate.setTime(endDate.getTime() + 3600 * 1000);
			}
			endDate.setMinutes(0, 0, 0);

			// Fetch from yesterday's day shift to cover both today and yesterday's same period
			let startDate = this.analyticsStore.getPreviousDayShiftDatetime();
			let endDateDB = formatDateTimeWithSeconds(endDate);
			let startDateDB = formatDateTimeWithSeconds(startDate);
			this.analyticsStore.getStatsViewsAggregated(startDateDB, endDateDB, callback, true);
		},
		updateViews() {
			this.isLoading = true;

			let startDate = this.periodDates.previousStart;
			let endDate = this.periodDates.end;

			if(startDate) {
				let endDateDB = formatDateTimeWithSeconds(endDate);
				let startDateDB = formatDateTimeWithSeconds(startDate);
				
				// Fetch aggregated data for the main period and previous period
				this.analyticsStore.getStatsViewsAggregated(startDateDB, endDateDB, () => {
					this.isLoading = false;
				});

			} else {
				this.isLoading = false;
			}

		},
		updateAll() {
			this.isLoading = true;

			if(Object.values(this.menuStore.menus).length > 0) {
				this.updateTodayViews();
				this.updateViews();
			} else {
				this.menuStore.refreshMenusOnly(() => {
					this.updateTodayViews();
					this.updateViews();
				});
			}

		},
		changedTimeframe(start, end, previousStart, previousEnd, timeframeIndex) {
			this.periodDates.start = start;
			this.periodDates.end = end;
			this.periodDates.previousStart = previousStart;
			this.periodDates.previousEnd = previousEnd;
			this.periodDates.timeframeIndex = timeframeIndex;
			this.updateViews();
		},
		scheduleNextRefresh() {
			// Clear any existing interval
			if(this.realtimeInterval) {
				clearInterval(this.realtimeInterval);
			}
			
			// Schedule the next refresh in 70 seconds
			this.realtimeInterval = setInterval(() => {
				this.updateTodayViews();
			}, 1000 * 70);
		}
	},
	mounted() {

		if (this.analyticsStore.loadedViews === false) {
			this.analyticsStore.loadedViews = true;
			this.periodDates.start = this.$refs["filters"].targetStartDate;
			this.periodDates.end = this.$refs["filters"].targetEndDate;
			this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
			this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
			this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
			this.updateAll();
		} else {
			// wait for the animation to finish
			setTimeout(() => {
				if(this.$refs["filters"]) {
					this.periodDates.start = this.$refs["filters"].targetStartDate;
					this.periodDates.end = this.$refs["filters"].targetEndDate;
					this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
					this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
					this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
				}
				this.updateTodayViews();
			}, 1000);
		}
		this.scheduleNextRefresh();

	},
	beforeUnmount() {
		clearInterval(this.realtimeInterval);
	},
	watch: {
		"userStore.currBusinessId": function (newVal, oldVal) {
			if(newVal != null && newVal !== oldVal) {
				this.updateAll();
			}
		},
		periodDates: {
			handler() {
				this.updateHeatMap();
			},
			deep: true
		},
		providedAction(newProvidedObject) {
			if(newProvidedObject.hasOwnProperty('showMobilePreview')) {
				if(newProvidedObject.showMobilePreview) {
					this.openHeatMap();
				}
			}
		},
	}

}
</script>

<style lang="scss" scoped>
.views-analytics-container {
	max-width: 1400px;
	margin: 0 auto;

	.preview-container {
		display: flex;
		justify-content: center;
		width: 100%;
		flex-direction: column;
		align-items: flex-start;

		&.shownMap {
			align-items: center;

			.base-button {
				display: none;
			}
		}

		.preview-phone-container {
			width: 252px;
			display: flex;
			justify-content: center;

			@media(max-width: 1500px) {
				width: 272px;
			}

			@media(max-width: 1200px) {
				width: 218px;
			}

			@media(max-width: 960px) {
				width: 210px;

				.container-preview {
					padding: 0;
				}
			}
		}
	}
}
</style>